import { ReactElement, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { AssetAllocationPerModuleChart } from "../AssetAllocationPerModuleChart";
import { usePortfolioGroupParams } from "@app/hooks/usePortfolioGroupParams";
import {
  CarPortfolioAssetColumnEnum,
  usePortfolioGroupVersionData,
  UsePortfolioGroupVersionDataParams,
} from "../../usePortfolioGroupVersionData";
import { usePortfolioGroupInfo } from "../../usePortfolioGroupInfo";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { CarAssetClassLevelField } from "@app/components/Inputs";
import { useSessionStorage } from "@app/hooks/useLocalStorage";
import { useChartDataSelector } from "../useChartDataSelector";
import { AssetAllocationPerModuleTable } from "../AssetAllocationPerModuleTable";
import { AssetClassLevel } from "@app/const";
import { SelectAssetColumnField } from "../SelectAssetColumnField";
import { InternalUseWithDisclosures } from "../InternalUse";
import { CarSwitch } from "@app/components/Switch";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import { openInNewTab } from "@app/utils";
import { CarDownloadButton } from "@app/components/Buttons";
import { assetAllocationPages } from "@app/const/route.const";

export const AnalyzeSummary_assetClassLevel = "AnalyzeSummary_assetClassLevel";
interface AnalyzeSummaryProps {
  reportFocusSelector: ReactElement;
}

export const AnalyzeSummary = (props: AnalyzeSummaryProps) => {
  const {
    portfolioGroupId,
    portfolioGroupVersionId,
    isViewPortfolioGroupVersionMode,
  } = usePortfolioGroupParams();

  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });

  const params: UsePortfolioGroupVersionDataParams =
    isViewPortfolioGroupVersionMode
      ? {
          versionId: portfolioGroupVersionId,
          publishedVersionId: undefined,
          hideCash: true,
          isPublishedOnly: true,
          show: {
            benchmark: true,
            existing: false,
            draft: true,
            selected: false,
          },
        }
      : {
          versionId: groupInfo.portfolioGroupVersionId,
          publishedVersionId: groupInfo.publishedVersionId,
          hideCash: true,
        };

  const data = usePortfolioGroupVersionData(params);

  const chartDataSelector = useChartDataSelector({
    storageKey: "AnalyzeSummary",
    data1DefaultId: CarPortfolioAssetColumnEnum.selected,
  });

  const [assetClassLevel, setAssetClassLevel] =
    useSessionStorage<AssetClassLevel>(
      AnalyzeSummary_assetClassLevel,
      AssetClassLevel.level1,
    );

  const [hideZeroValues, setHideZeroValues] = useSessionStorage<boolean>(
    "AnalyzeSummary_hideZeroValues",
    false,
  );

  const selectorItems =
    data.tableData.bands[0]?.columns.map((col) => ({
      label:
        col.columnId === CarPortfolioAssetColumnEnum.selected
          ? "Selected"
          : col.label,
      value: col.columnId,
    })) ?? [];

  const handleShowPdf = useCallback(() => {
    if (portfolioGroupId && groupInfo.portfolioGroupVersionId) {
      openInNewTab(
        assetAllocationPages.getPdfSummaryReportUrl({
          portfolioGroupId,
          data1Id: chartDataSelector.data1Id ?? "",
        }),
      );
    }
  }, [
    chartDataSelector.data1Id,
    portfolioGroupId,
    groupInfo.portfolioGroupVersionId,
  ]);

  return (
    <TablePlaceHolder isLoading={data.isLoading} rows={8} sx={{ mt: 5 }}>
      <Box sx={{ mt: 5, display: "flex", gap: 3.5 }}>
        {props.reportFocusSelector}
        <CarAssetClassLevelField
          sx={{
            width: 150,
            ".MuiInputLabel-root": {
              fontSize: "16px",
            },
            ".MuiOutlinedInput-root": {
              mt: 3,
            },
            ".MuiInputBase-input": {
              backgroundColor: "gray1",
            },
          }}
          label={
            <>
              Asset Class Level
              <CarTooltipBox
                sx={{ display: "inline-block", ml: 0.5, mb: -0.5 }}
                className={pendoClasses.editPortfolioAnalyzeAssetClassLevel}
              />
            </>
          }
          value={assetClassLevel}
          onChange={setAssetClassLevel}
        />
        <SelectAssetColumnField
          sx={{
            width: 150,
            ".MuiInputLabel-root": {
              fontSize: "16px",
            },
            ".MuiOutlinedInput-root": {
              mt: 3,
            },
            ".MuiInputBase-input": {
              backgroundColor: "gray1",
            },
          }}
          label={
            <>
              Data
              <CarTooltipBox
                sx={{ display: "inline-block", ml: 0.5, mb: -0.5 }}
                className={pendoClasses.editPortfolioAnalyzeData}
              />
            </>
          }
          value={chartDataSelector.data1Id}
          onChange={chartDataSelector.onData1IdChange}
          items={selectorItems}
        />
        <InternalUseWithDisclosures sx={{ ml: "auto" }} />
      </Box>
      <CarDownloadButton
        onClick={handleShowPdf}
        sx={{ zIndex: 1, mt: 2, mr: -1, mb: -3, alignSelf: "flex-end" }}
      >
        Download Reports
      </CarDownloadButton>

      <AssetAllocationPerModuleChart
        sx={{ mt: 8, alignSelf: "center" }}
        columnId={chartDataSelector.data1Id ?? ""}
        level={assetClassLevel}
        tableData={data.tableData}
      />
      <Box
        sx={{
          mt: 4,
          ml: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Typography variant="par02Regular">Hide 0 Values</Typography>
        <CarSwitch
          checked={hideZeroValues}
          onChange={(e, value) => setHideZeroValues(value)}
        />
      </Box>
      <AssetAllocationPerModuleTable
        sx={{ mt: 1 }}
        columnId={chartDataSelector.data1Id ?? ""}
        level={assetClassLevel}
        hideZeroValues={hideZeroValues}
        tableData={data.tableData}
      />
    </TablePlaceHolder>
  );
};
